import { DialogContent, DialogTitle, Stack } from '@mui/material';
import { Button, IconButton, Typography } from '@sweep-io/sweep-design';
import { Close } from '@sweep-io/sweep-design/dist/icons';
import { SetStateAction, useCallback, useEffect, useState } from 'react';
import { InstructionsEmptyState } from './InstructionsEmptyState';
import { InstructionsList } from './InstructionsList';
import { useSelector } from 'react-redux';
import { selectAiSupportInstructions } from '../../../../reducers/aiSupportReducer';
import EditableInstructionCard from './EditableInstructionCard';
import { useInstructions } from './useInstructions';
import { AiSupportInstructionProperties } from '@server/ai';
import { selectDefaultCreationCrmOrgId } from '../../../../reducers/userInfoReducer';
import { useRunOnceWhenTruthy } from '../../../common/useRunOnceWhenTruthy';
import { CenteredCircularProgress } from '../../../common/CenteredCircularProgress';
import useCheckPermissionsForCurrentCrmOrg from '../../../common/permissions/useCurrentCrmOrgPermissions';
import RestrictedTooltip from '../../../common/permissions/RestrictedTooltip';
import { useFeatureToggle } from '../../../../feature-flags/useFeatureToggle';

interface InstructionsContentProps {
  onClose: (e: any) => void;
  setEditableCardPristineStates: (state: SetStateAction<Record<string, boolean>>) => void;
  setIsCreatingNew: (state: boolean) => void;
  isCreatingNew: boolean;
  setNewCardIsPristine: (state: boolean) => void;
}

export const InstructionsContent = ({
  onClose,
  setEditableCardPristineStates,
  setIsCreatingNew,
  isCreatingNew,
  setNewCardIsPristine,
}: InstructionsContentProps) => {
  const { supportAgentInAgenticCenter } = useFeatureToggle();
  const [isLoading, setIsLoading] = useState(false);
  const defaultCrmOrgId = useSelector(selectDefaultCreationCrmOrgId);
  const instructions = useSelector(selectAiSupportInstructions(defaultCrmOrgId ?? ''));

  const newInstruction = {
    id: '',
    name: '',
    content: '',
    crmOrgId: defaultCrmOrgId,
  };

  const { createInstruction, deleteInstruction, fetchInstructions } = useInstructions();
  const [isCreateAllowed] = useCheckPermissionsForCurrentCrmOrg(['create:ai-sessions']);

  useRunOnceWhenTruthy(() => {
    setIsLoading(true);
    fetchInstructions().finally(() => setIsLoading(false));
  }, !!defaultCrmOrgId && !instructions);

  useEffect(() => {
    return () => setIsCreatingNew(false);
  }, [setIsCreatingNew]);

  const isEmpty = instructions?.length === 0 && !isCreatingNew;

  const createInstructionAndRemoveCreationCard = useCallback(
    async (instruction: AiSupportInstructionProperties) => {
      await createInstruction(instruction);
      setIsCreatingNew(false);
    },
    [createInstruction, setIsCreatingNew],
  );

  const onPristineChange = useCallback(
    (id: string, isPristine: boolean) => {
      setEditableCardPristineStates((prev) => ({ ...prev, [id]: isPristine }));
    },
    [setEditableCardPristineStates],
  );

  return (
    <>
      <DialogTitle sx={{ padding: supportAgentInAgenticCenter ? '16px' : undefined }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Typography variant="h3">
            {supportAgentInAgenticCenter ? 'Custom Instructions' : 'Instructions'}
          </Typography>

          <Stack direction="row" gap={1} alignItems="center">
            {instructions?.length !== 0 && !isLoading && (
              <RestrictedTooltip
                to={['create:ai-sessions']}
                notAllowedTitle="To create a new instruction, please contact your admin"
              >
                <Button
                  startIconName="Plus"
                  size="small"
                  disabled={!isCreateAllowed}
                  onClick={() => setIsCreatingNew(true)}
                >
                  Create New
                </Button>
              </RestrictedTooltip>
            )}

            <IconButton variant="flat" size="small" onClick={onClose}>
              <Close />
            </IconButton>
          </Stack>
        </Stack>
      </DialogTitle>

      <DialogContent sx={{ padding: supportAgentInAgenticCenter ? '16px' : undefined }}>
        {isLoading ? (
          <CenteredCircularProgress />
        ) : isEmpty ? (
          <InstructionsEmptyState onCreate={() => setIsCreatingNew(true)} />
        ) : (
          <>
            {isCreatingNew && (
              <EditableInstructionCard
                key="new-card"
                card={newInstruction as AiSupportInstructionProperties}
                defaultEditMode={true}
                isDeleteAllowed={false}
                isUpdateAllowed={false}
                onUpdateOrCreate={createInstructionAndRemoveCreationCard}
                onRemove={deleteInstruction}
                onCancel={() => setIsCreatingNew(false)}
                onIsPristineChange={(id, isPristine) => setNewCardIsPristine(isPristine)}
              />
            )}

            <InstructionsList instructions={instructions} onIsPristineChange={onPristineChange} />
          </>
        )}
      </DialogContent>
    </>
  );
};
